// src/components/AccountSelect.js — shared Chart of Accounts helpers
// [marker: coa v1]
// <AccountSelect value onChange types={['income','other_income']} placeholder />
//   dropdown of accounts grouped by type, sub-accounts indented (QuickBooks style)
// useAccounts({ includeInactive }) → { accounts, reload }   (flat list with depth + fullName)
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

export const TYPE_ORDER = [
  'bank', 'accounts_receivable', 'asset', 'fixed_asset', 'other_asset',
  'accounts_payable', 'credit_card', 'other_liability', 'loan',
  'equity', 'income', 'other_income', 'cost_of_goods_sold', 'expense', 'other_expense',
];
export const TYPE_LABELS = {
  bank: 'Bank',
  accounts_receivable: 'Accounts Receivable',
  asset: 'Other Current Asset',
  fixed_asset: 'Fixed Asset',
  other_asset: 'Other Asset',
  accounts_payable: 'Accounts Payable',
  credit_card: 'Credit Card',
  other_liability: 'Other Current Liability',
  loan: 'Long Term Liability / Loan',
  equity: 'Equity',
  income: 'Income',
  other_income: 'Other Income',
  cost_of_goods_sold: 'Cost of Goods Sold',
  expense: 'Expense',
  other_expense: 'Other Expense',
};
export const BALANCE_SHEET_TYPES = ['bank', 'accounts_receivable', 'asset', 'fixed_asset', 'other_asset',
  'accounts_payable', 'credit_card', 'other_liability', 'loan', 'equity'];
// Accounts whose normal balance is a credit (show their balance as a positive number)
export const CREDIT_NORMAL = ['accounts_payable', 'credit_card', 'other_liability', 'loan', 'equity',
  'income', 'other_income'];

// Order a flat list into a tree walk: type order, then parent → children, with depth + "Parent:Child" names
export function orderAccounts(list) {
  const byParent = new Map();
  const ids = new Set(list.map(a => a.id));
  list.forEach(a => {
    const p = a.parent_account_id && ids.has(a.parent_account_id) ? a.parent_account_id : 0;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(a);
  });
  const sortFn = (a, b) => (a.sort_order - b.sort_order) || String(a.name).localeCompare(String(b.name));
  for (const arr of byParent.values()) arr.sort(sortFn);
  const out = [];
  const walk = (a, depth, prefix) => {
    const fullName = prefix ? `${prefix}:${a.name}` : a.name;
    out.push({ ...a, depth, fullName });
    (byParent.get(a.id) || []).forEach(ch => walk(ch, depth + 1, fullName));
  };
  TYPE_ORDER.forEach(t => (byParent.get(0) || []).filter(a => a.account_type === t).forEach(a => walk(a, 0, '')));
  // any type not in TYPE_ORDER (shouldn't happen) goes last
  (byParent.get(0) || []).filter(a => !TYPE_ORDER.includes(a.account_type)).forEach(a => walk(a, 0, ''));
  return out;
}

let cache = { key: null, data: null, at: 0 };

export function useAccounts({ includeInactive = false } = {}) {
  const key = includeInactive ? 'all' : 'active';
  const [accounts, setAccounts] = useState(() => (cache.key === key ? cache.data : null) || []);
  const reload = useCallback(() => {
    return api.get(`/bookkeeping/accounts?flat=true&activeOnly=${includeInactive ? 'false' : 'true'}`)
      .then(r => {
        const ordered = orderAccounts(r.data || []);
        cache = { key, data: ordered, at: Date.now() };
        setAccounts(ordered);
        return ordered;
      })
      .catch(() => []);
  }, [includeInactive, key]);
  useEffect(() => {
    if (cache.key === key && cache.data && Date.now() - cache.at < 30000) setAccounts(cache.data);
    else reload();
  }, [key, reload]);
  return { accounts, reload };
}

export function invalidateAccounts() { cache = { key: null, data: null, at: 0 }; }

export function AccountSelect({ value, onChange, types, placeholder = '— Choose an account —', disabled, style, accounts: given, excludeIds }) {
  const { accounts: loaded } = useAccounts();
  const all = given || loaded;
  const list = all.filter(a => (!types || types.includes(a.account_type)) && !(excludeIds || []).map(String).includes(String(a.id)));
  const groups = [];
  list.forEach(a => {
    let g = groups[groups.length - 1];
    if (!g || g.type !== a.account_type) { g = { type: a.account_type, items: [] }; groups.push(g); }
    g.items.push(a);
  });
  return (
    <select className="form-select" value={value || ''} disabled={disabled} style={style}
            onChange={e => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}>
      <option value="">{placeholder}</option>
      {groups.map(g => (
        <optgroup key={g.type} label={TYPE_LABELS[g.type] || g.type}>
          {g.items.map(a => (
            <option key={a.id} value={a.id}>{'   '.repeat(a.depth)}{a.account_number ? a.account_number + ' · ' : ''}{a.name}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export function fmtMoney(n) {
  const v = parseFloat(n || 0);
  return (v < 0 ? '-' : '') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Balance as a person reads it: assets/expenses debit-positive, liabilities/equity/income credit-positive
export function displayBalance(a) {
  const raw = parseFloat(a.balance ?? 0);
  return CREDIT_NORMAL.includes(a.account_type) ? -raw : raw;
}
